<template>
  <StepDescriptionModal
    :open="isStepDescriptionModalOpen"
    flow-type="change"
    :step-name="changeInvestmentStore.currentStep?.name"
    :substep-name="
      changeInvestmentStore.currentStep?.sub_steps?.[changeInvestmentStore.current_substep - 1]
    "
    @close-modal="isStepDescriptionModalOpen = false"
  />
  <CI_CancelChangeInvestmentModal
    :open="showCancelModal"
    @close-modal="showCancelModal = false"
    @confirm-cancel="$emit('cancel-change')"
  />
  <div class="flex flex-col gap-6 mt-6 lg:mt-10">
    <!-- Top Section w/ Back CTA and Change Investment Metric CTA's -->
    <div class="flex items-center justify-between">
      <div class="flex gap-4 items-center">
        <Button @click="$emit('back')" size="sm" variant="paper-primary">
          <template #icon>
            <Icon name="ChevronLeft" class="h-4 w-4" :stroke-width="3" />
          </template>
        </Button>
        <div class="flex items-center gap-2 h-6 rounded-[117px] bg-navbar-invest-why-info px-4">
          <Icon name="Save" class="h-4 w-4 text-strong" />
          <p class="text-strong font-medium font-inter text-sm whitespace-nowrap">
            Your Progress is being saved
          </p>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-between">
      <div class="flex flex-col lg:gap-2">
        <p class="text-strongest text-sm lg:text-base font-medium font-inter">
          Step {{ changeInvestmentStore?.current_step }}
          <span class="text-strong">
            of {{ changeInvestmentStore?.change_investment_steps?.length }}
          </span>
        </p>
        <p class="text-2xl lg:text-4xl tracking-[-.72px] font-medium text-strongest">
          {{
            changeInvestmentStore?.change_investment_steps[changeInvestmentStore?.current_step - 1]
              ?.label
          }}
        </p>
      </div>
      <div class="items-center gap-2 hidden lg:flex">
        <Button @click="isStepDescriptionModalOpen = true" size="lg" variant="paper-primary">
          <template #icon>
            <Icon name="CircleQuestionMark" class="h-6 w-6" />
          </template>
        </Button>
        <Button
          v-if="isLastStep"
          @click="showCancelModal = true"
          variant="paper-primary-neo"
          size="lg"
          class="w-full sm:w-auto max-w-[400px]"
        >
          <template #label> Cancel Change </template>
        </Button>
        <Button @click="$emit('continue')" size="lg" variant="primary-secondary-neo">
          <template #label>
            {{ continueButtonText }}
          </template>
        </Button>
      </div>
    </div>
  </div>

  <!-- Mobile Fixed Bottom Bar -->
  <div
    class="fixed bottom-0 left-0 right-0 lg:hidden btn-bg-swap bg-purchase-flow-bottom-nav-gradient p-3 flex flex-col justify-center rounded-t-3xl z-50"
    :class="showAllocationTracker ? 'h-auto' : 'h-[70px]'"
  >
    <!-- Funds Allocation Tracker (mobile) -->
    <div v-if="showAllocationTracker" class="flex flex-col gap-2 mb-4">
      <div class="flex items-center justify-between">
        <h3 class="font-semibold font-inter text-strongest">Funds allocation</h3>
        <p
          class="font-inter font-medium text-sm"
          :class="
            showAllocationError && amountRemaining > 0
              ? 'text-error'
              : amountRemaining === 0
                ? 'text-success'
                : 'text-strongest'
          "
        >
          Amount Remaining: ${{ commaSeparateThousands(amountRemaining) }}
        </p>
      </div>
      <div
        class="w-full h-2 bg-white rounded-full overflow-hidden border"
        :class="trackerBarStyles.border"
      >
        <div
          class="h-full rounded-full transition-all duration-300 ease-out"
          :class="trackerBarStyles.fill"
          :style="{ width: `${trackerPercentage}%` }"
        />
      </div>
    </div>

    <!-- Continue, Help & Add Split Row -->
    <div class="flex items-center justify-between gap-2">
      <Button @click="isStepDescriptionModalOpen = true" variant="paper-primary">
        <template #icon>
          <Icon name="CircleQuestionMark" class="h-6 w-6" />
        </template>
      </Button>
      <Button
        v-if="showAllocationTracker"
        @click="changeInvestmentStore.addNewInvestmentForSplit"
        variant="paper-primary-neo"
        class="w-full sm:w-auto max-w-[400px]"
      >
        <template #label>Add a split</template>
      </Button>
      <Button
        v-if="isLastStep && !changeInvestmentStore.allDocumentsSigned"
        @click="showCancelModal = true"
        variant="paper-primary-neo"
        class="w-full sm:w-auto max-w-[400px]"
      >
        <template #label> Cancel Change </template>
      </Button>
      <Button
        v-if="!isLastStep || !changeInvestmentStore.allDocumentsSigned"
        @click="$emit('continue')"
        variant="primary-secondary-neo"
        class="w-full sm:w-auto max-w-[400px]"
      >
        <template #label>
          {{ continueButtonText }}
        </template>
      </Button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useChangeInvestmentStore } from "@/stores";
import { Button, Icon } from "@/components";
import { commaSeparateThousands } from "@/utilities";
import StepDescriptionModal from "../PurchaseFlow/PurchaseFlowUtilities/StepDescriptionModal.vue";
import CI_CancelChangeInvestmentModal from "./CI_CancelChangeInvestmentModal.vue";

const changeInvestmentStore = useChangeInvestmentStore();

defineEmits(["continue", "back", "cancel-change"]);

const showCancelModal = ref(false);
const isStepDescriptionModalOpen = ref(false);
const showAllocationError = ref(false);

const isLastStep = computed(() => {
  return (
    changeInvestmentStore.current_step === changeInvestmentStore.change_investment_steps.length
  );
});

const continueButtonText = computed(() => {
  if (!isLastStep.value) return "Continue";
  return "Finish Later";
});

const showAllocationTracker = computed(() => {
  return changeInvestmentStore.current_step === 1;
});

const investmentForChange = computed(() => {
  return changeInvestmentStore.current_investment_for_change;
});

const amountRemaining = computed(() => {
  const available = Math.floor(investmentForChange.value.current_nav / 1000) * 1000;
  return Math.max(available - changeInvestmentStore.totalChildrenAmount, 0);
});

const trackerPercentage = computed(() => {
  if (changeInvestmentStore.investableAmount === 0) return 0;
  const pct =
    (changeInvestmentStore.totalChildrenAmount / changeInvestmentStore.investableAmount) * 100;
  return Math.min(pct, 100);
});

const trackerBarStyles = computed(() => {
  if (showAllocationError.value && amountRemaining.value > 0) {
    return { fill: "bg-error", border: "border-error" };
  }
  if (amountRemaining.value === 0) {
    return { fill: "bg-success", border: "border-success" };
  }
  return { fill: "bg-filter-bg-selected-chip", border: "border-radio-checkbox-outline" };
});
</script>
